/**
 * Registry of valid `data-walkthrough` anchor ids — the contract between UI and tour steps.
 * @module features/walkthrough/walkthroughAnchors
 */

import { NAV_SECTIONS } from '../../navigation/navConfig';

/** @type {readonly string[]} */
export const LAYOUT_WALKTHROUGH_ANCHORS = Object.freeze([
  'welcome-anchor',
  'nav-sidebar',
  'nav-mobile-menu',
  'nav-sidebar-toggle',
  'theme-profile',
  'nav-sections',
]);

/** @type {readonly string[]} */
export const PAGE_WALKTHROUGH_ANCHORS = Object.freeze([
  'timesheet-month-nav-desktop',
  'timesheet-month-nav-mobile',
  'timesheet-add-entry-desktop',
  'timesheet-add-entry-mobile',
  'timesheet-hours-summary-desktop',
  'timesheet-hours-summary-mobile',
  'timesheet-submit-desktop',
  'timesheet-submit-mobile',
  'review-timesheets-filters',
  'review-approve-actions',
  'review-read-only-hint',
  'admin-dashboard-stats',
  'my-skill-matrix-actions',
  'manage-skills-actions',
  'resource-finder-actions',
  'audit-log-filters',
  'audit-log-table',
  'audit-log-admin-scope',
  'approved-timesheets-export',
]);

/** @type {readonly string[]} */
export const NAV_ITEM_WALKTHROUGH_ANCHORS = Object.freeze(
  NAV_SECTIONS.flatMap((section) => section.items.map((item) => `nav-${item.id}`))
);

/** @type {readonly string[]} */
export const NAV_SECTION_WALKTHROUGH_ANCHORS = Object.freeze(
  NAV_SECTIONS.map((section) => `nav-section-${section.id}`)
);

/** @type {readonly string[]} */
export const ALL_WALKTHROUGH_ANCHOR_IDS_LIST = Object.freeze([
  ...LAYOUT_WALKTHROUGH_ANCHORS,
  ...NAV_SECTION_WALKTHROUGH_ANCHORS,
  ...NAV_ITEM_WALKTHROUGH_ANCHORS,
  ...PAGE_WALKTHROUGH_ANCHORS,
]);

/** @type {ReadonlySet<string>} */
export const WALKTHROUGH_ANCHOR_IDS = new Set(ALL_WALKTHROUGH_ANCHOR_IDS_LIST);

/**
 * @param {string | null | undefined} selector
 * @returns {string | null}
 */
export function extractWalkthroughAnchorId(selector) {
  if (!selector || typeof selector !== 'string') return null;
  const match = selector.match(/\[data-walkthrough="([^"]+)"\]/);
  return match ? match[1] : null;
}

/**
 * @param {import('./walkthroughTypes').WalkthroughStepDef} step
 * @returns {string[]}
 */
export function collectStepAnchorIds(step) {
  return [step.targetSelector, step.mobileSelector, step.desktopSelector]
    .map(extractWalkthroughAnchorId)
    .filter(Boolean);
}
